import React from 'react';
import { Box, Divider, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BuwlSourceSystem } from '../../model/IBuwlCKOContext';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Payment, PaymentType } from '../../model/Payment';
import { formatAmount } from '../../util/currencyUtils';
import { useBuwlCKOContext } from '../../context/GlobalBuwlCkoAppContext';

interface PriceSummaryProps {
    subtotal: string;
    shipping: string;
    tax: string;
    payment: Payment;
    sourceSystem: BuwlSourceSystem;
}

const PriceSummary: React.FC<PriceSummaryProps> = ({ subtotal, shipping, tax, payment, sourceSystem }) => {
    const { t } = useTranslation();
    const { buwlContext, updateContextValue } = useBuwlCKOContext();

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{t('sum')}</Typography>
                <Typography sx={{ fontFamily: '"Forum", serif' }} variant="subtitle1">
                    {subtotal}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{t('delivery')}</Typography>
                <Typography sx={{ fontFamily: '"Forum", serif' }} variant="subtitle1">
                    {shipping}
                </Typography>
            </Box>
            <Divider />
            {payment.paymentType == PaymentType.ADVANCE_PAYMENT && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="h6">{t('advancePayment')}</Typography>
                    <Typography sx={{ fontFamily: '"Forum", serif', textTransform: 'lowercase' }} variant="h6">
                        {formatAmount(payment.prepayment, buwlContext.sourceSystem)}
                    </Typography>
                </Box>
            )}
            {payment.paymentType == PaymentType.ADVANCE_PAYMENT && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="h6">{t('cacheOnDelivery')}</Typography>
                    <Typography sx={{ fontFamily: '"Forum", serif', textTransform: 'lowercase' }} variant="h6">
                        {formatAmount(payment.COD, buwlContext.sourceSystem)}
                    </Typography>
                </Box>
            )}
            {payment.paymentType == PaymentType.ONLINE && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="h6">{t('total')}</Typography>
                    <Typography sx={{ fontFamily: '"Forum", serif', textTransform: 'lowercase' }} variant="h6">
                        {formatAmount(payment.total, buwlContext.sourceSystem)}
                    </Typography>
                </Box>
            )}

            {sourceSystem === BuwlSourceSystem.BUWL_WEB_PL && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Typography variant="body2">{t('PLN_Payment_Note')}</Typography>
                </Box>
            )}
        </Box>
    );
};

export default PriceSummary;
